import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';

// ─── LockedPage — the app-wide access lock's "No active plan" page ────────────────────────────
// Rendered by App.tsx's LockGate INSTEAD OF the normal AppLayout shell (no Sidebar/TopBar) for any
// non-admin whose org/facility has no active Stripe subscription and no active admin bypass (v143,
// server/src/lib/planTier.ts's getAccessLockState). A standalone page, not a route inside the app
// shell, since a locked user has nothing else in the shell to see. The one way forward is Billing &
// Plan (always reachable regardless of lock state -- LockGate exempts it, and the server's
// authenticate middleware exempts /api/billing/* the same way) to self-subscribe; the copy below
// says as much without assuming the viewer CAN manage billing (a locked manager under Centralized
// mode is read-only there and needs to contact their org_admin instead -- Billing.tsx already shows
// that distinction correctly once they land on it, so this page doesn't duplicate that logic).
export default function LockedPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canLikelyManage = user?.role === 'org_admin' || user?.role === 'manager';

  return (
    // ROOT CAUSE of the "pinned to the upper-left" bug: #root (global.css) is `display: flex` with
    // the default flex-direction row -- a plain block div with no explicit width shrink-wraps to
    // its CONTENT width as a flex item instead of filling the viewport, so this div's own internal
    // centering only centered within that narrow sliver. .app-shell (AppLayout's own root, global.css)
    // hits the exact same #root constraint and fixes it the same way: an explicit `width: 100%`.
    <div style={{ width: '100%', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'var(--bg-base)' }}>
      <div className="card" style={{ maxWidth: 460, width: '100%', padding: '40px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Lock size={26} color="#ef4444" />
        </div>
        <h1 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700, color: 'var(--text-primary)' }}>No Active Plan</h1>
        <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {user?.orgName ? <>{user.orgName}&rsquo;s</> : 'Your organization’s'} subscription isn&rsquo;t active, so access to ProQure is paused.
          {' '}Nothing has been deleted or changed &mdash; everything picks back up exactly where it left off the moment a plan is active again.
        </p>
        {canLikelyManage ? (
          <button className="btn btn-primary" style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 8 }} onClick={() => navigate('/billing')}>
            Go to Billing &amp; Plan <ArrowRight size={15} />
          </button>
        ) : (
          <p style={{ margin: '6px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            Contact your organization administrator to reactivate your plan.
          </p>
        )}
        {/* Everyone gets the link regardless -- Billing.tsx itself correctly shows read-only /
            Restricted messaging for a role that can't manage the plan, so this page doesn't need
            to duplicate that role logic, just always offer the door. */}
        {!canLikelyManage && (
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/billing')}>
            View Billing &amp; Plan
          </button>
        )}
        <button
          className="btn btn-ghost btn-sm"
          style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)' }}
          onClick={logout}
        >
          <LogOut size={13} /> Log Out
        </button>
      </div>
    </div>
  );
}
